import { useCallback } from "react";
import { chain } from "lodash";
import { LayerTypeSelectionDetailViewProps } from "react-migration/layouts/map/Multilayer/types";
import { useAttributeTranslation } from "react-migration/domains/constraints/hooks/useAttributeTranslation";
import { ConstraintsCategory } from "react-migration/layouts/map/Constraints/types";
import { useDesignationById } from "react-migration/domains/constraints/designation/hooks/useDesignationById";
import { SkeletonLoading } from "react-migration/components/SkeletonLoading";
import { DataPanel } from "react-migration/components/DataPanel";
import {
  SingleDesignation,
  SingleDesignationDesignationAttribute,
} from "react-migration/domains/constraints/typings/applicationTypes/SingleDesignation";
import { SUBSTATION_ATTRIBUTE_RENDERERS } from "./attributeRenderers";
import { SubstationAttribution } from "./SubstationAttribution";
import { AttributeRendersMap } from "./types";
import { SUBSTATION_ATTRIBUTE_ORDER } from "./constants";

export function SubstationSelectionDetailView({ selection }: LayerTypeSelectionDetailViewProps) {
  const { designation, ...designationQuery } = useDesignationById(selection.id);

  if (designationQuery.loading || !designation) {
    return (
      <div className="overflow-auto bg-white">
        <div className="relative m-3 min-h-96" data-testid="designation-body-loading">
          <SkeletonLoading rows={3} showPadding={false} />
        </div>
      </div>
    );
  }

  return (
    <>
      <DesignationDetailView
        designation={designation}
        translationCategory={ConstraintsCategory.SUB_STATIONS}
        attributeRenderers={SUBSTATION_ATTRIBUTE_RENDERERS}
        attributeOrder={SUBSTATION_ATTRIBUTE_ORDER}
      />
      <SubstationAttribution designation={designation} />
    </>
  );
}

interface DesignationDetailViewProps {
  designation: SingleDesignation;
  /**
   * Used to namespace the translation keys for the attribute labels
   * @example "constraints.translation_category_here.attributes.attribute_key_here"
   */
  translationCategory: ConstraintsCategory;
  /**
   * Used to alter the display of attributes.
   */
  attributeRenderers?: AttributeRendersMap;
  /**
   * List of attribute keys in the order they should be displayed.
   */
  attributeOrder?: string[];
}

const HEADLINE_ATTRIBUTE_GROUP_CODE = "substation_headline";

function DesignationDetailView({
  designation,
  translationCategory,
  attributeRenderers,
  attributeOrder,
}: DesignationDetailViewProps) {
  const translateKey = useAttributeTranslation(false, translationCategory);

  const renderAttribute = useCallback(
    (attribute: SingleDesignationDesignationAttribute) => {
      const hasCustomRenderer = !!attributeRenderers?.[attribute.key];
      const rendered = attributeRenderers?.[attribute.key]?.(attribute);
      const value = hasCustomRenderer ? rendered : attribute.value;

      return typeof value === "string" ? (
        <span className="text-sm font-medium text-right wrap-break-word">{value}</span>
      ) : (
        value
      );
    },
    [attributeRenderers]
  );

  if (!designation.designation_attributes.length) {
    return null;
  }

  const { designation_attributes } = designation;
  const designationAttributesOrdered = attributeOrder
    ? chain(designation_attributes)
        .sortBy((attribute) => attributeOrder.indexOf(attribute.key))
        .value()
    : designation_attributes;

  const attributeGroupDisplayNames = chain(designationAttributesOrdered)
    .map("designation_attribute_group")
    .compact()
    .uniqBy("code")
    .keyBy("code")
    .mapValues("display_name")
    .value();

  const attributesByGroup = chain(designationAttributesOrdered).groupBy("designation_attribute_group.code").value();

  const headlineAttributes = attributesByGroup[HEADLINE_ATTRIBUTE_GROUP_CODE] || [];
  const attributeGroups = chain(attributesByGroup)
    .omit(HEADLINE_ATTRIBUTE_GROUP_CODE)
    .toPairs()
    // move undefined "Other attributes" group to the end
    .sortBy(([attrGroupCode]) => attrGroupCode === "undefined")
    .value();

  return (
    <div className="flex flex-col" data-testid="designation-detail-view">
      {headlineAttributes.length > 0 && (
        <section className="px-3 py-2" data-testid="designation-detail-view__headline-attributes">
          <DataPanel.Grid columns={2}>
            {headlineAttributes.map((attribute) => (
              <DataPanel.Cell key={attribute.id} title={translateKey(attribute.key)} variant="light">
                {renderAttribute(attribute)}
              </DataPanel.Cell>
            ))}
          </DataPanel.Grid>
        </section>
      )}

      {attributeGroups.map(([groupCode, attributes]) => {
        const showMiscGroupHeading = attributeGroups.length > 1 || headlineAttributes.length > 0;
        const groupHeading = showMiscGroupHeading
          ? attributeGroupDisplayNames[groupCode] || "Other Attributes"
          : undefined;

        return (
          <section key={groupCode} className="px-3 py-2" data-testid="designation-detail-view__attribute-groups">
            <DataPanel.Cell title={groupHeading} variant="light">
              <div
                className="flex flex-col gap-2 mt-1"
                data-testid={`designation-detail-view__attribute-group__${groupCode}`}
              >
                {attributes.map((attribute) => (
                  <div
                    key={attribute.id}
                    className="flex items-end justify-between gap-2 text-xs text-text-base-primary"
                  >
                    <span className="font-normal shrink-0">{translateKey(attribute.key)}</span>
                    {renderAttribute(attribute)}
                  </div>
                ))}
              </div>
            </DataPanel.Cell>
          </section>
        );
      })}
    </div>
  );
}
